import { mergeAttributes } from '@tiptap/core';
import { ImageResize } from 'tiptap-extension-resize-image';

/**
 * Configured TipTap image-resize extension shared across all three email
 * composers (EmailComposer, EmailTemplateEditor, SequenceEmailComposer).
 *
 * Why this wrapper exists
 * -----------------------
 * `tiptap-extension-resize-image` adds click-to-resize handles (Google-Docs
 * style: drag the corner/edge dots to resize, aspect ratio preserved) on top
 * of the canonical `@tiptap/extension-image`. After resize it stores width in
 * the `containerStyle` node attribute as raw CSS text:
 *
 *     <img containerstyle="width: 400px; height: auto; cursor: pointer;" />
 *
 * That round-trips fine in the editor — but real email clients (Gmail, Outlook
 * desktop, Apple Mail) ignore the unknown `containerstyle` attribute and treat
 * the image as 0×0 / native size. Email-client compatibility wants BOTH:
 *
 *   - an HTML `width` attribute (no unit) — most universally honored, especially
 *     in legacy Outlook which uses Word's HTML renderer and trusts attrs over CSS
 *   - an inline `style` containing `width: Npx; height: auto; max-width: 100%`
 *     — for modern webmail clients that respect CSS and for mobile responsive
 *
 * This wrapper's `renderHTML` override:
 *   1. Calls upstream renderHTML to get the merged <img> attrs
 *   2. Parses width out of `containerStyle` (e.g. "width: 400px; ...")
 *   3. Adds a `width="400"` HTML attribute (without `px`) AND ensures the
 *      inline style carries `width: 400px; height: auto; max-width: 100%`
 *   4. Drops the `containerstyle` / `wrapperstyle` raw-CSS attributes from
 *      output — they're editor-internal scaffolding and email clients are
 *      better off without them
 *   5. Preserves alignment (margin: 0 auto / 0 0 0 auto) when present in
 *      containerStyle, by carrying it through to the final inline style
 */

interface CreateImageExtensionOptions {
  /** Render as inline element (true for one-off + sequence composer, false for template editor). */
  inline?: boolean;
  /** Allow `data:` URLs (true for one-off, which converts base64 → CID at send time). */
  allowBase64?: boolean;
  /** Min width in px the user can drag down to. Default 50. */
  minWidth?: number;
  /** Max width in px the user can drag up to. */
  maxWidth?: number;
  /** Tailwind/utility class string for the <img> tag. */
  htmlClass?: string;
}

// Pull a `width: 400px` (or `400.5px`) value out of a CSS text blob. Returns
// the numeric value as a string ("400") or null if no width:Npx found.
function extractWidthPx(style: string | null | undefined): string | null {
  if (!style) return null;
  const m = style.match(/width:\s*([0-9.]+)px/);
  return m ? m[1] : null;
}

// Pull a `margin: ...` declaration out of a CSS text blob. Used to preserve
// the package's alignment state (left / center / right) through serialization.
// Returns the full `margin: <value>` snippet without the trailing semicolon, or
// null if no margin found.
function extractMargin(style: string | null | undefined): string | null {
  if (!style) return null;
  const m = style.match(/margin:\s*([^;]+)/);
  return m ? `margin: ${m[1].trim()}` : null;
}

export function createImageExtension(options: CreateImageExtensionOptions = {}) {
  const {
    inline = false,
    allowBase64 = false,
    minWidth = 50,
    maxWidth,
    htmlClass = 'rounded-md max-w-full h-auto',
  } = options;

  return ImageResize.extend({
    /**
     * Override renderHTML to emit an email-client-safe <img>. The package's
     * default renderHTML (inherited from @tiptap/extension-image) calls
     * `mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)` and spreads
     * EVERY node attribute onto the <img> tag — including `containerStyle` and
     * `wrapperStyle` as raw HTML attributes. We rewrite that output here.
     */
    renderHTML({ HTMLAttributes }) {
      // mergeAttributes flattens our configured HTMLAttributes onto the
      // node's stored attributes the same way the upstream extension does.
      // This gives us a single attrs object to massage.
      const attrs = mergeAttributes(this.options.HTMLAttributes, HTMLAttributes) as Record<
        string,
        unknown
      >;

      const containerStyle =
        typeof attrs.containerstyle === 'string'
          ? attrs.containerstyle
          : typeof attrs.containerStyle === 'string'
            ? attrs.containerStyle
            : null;

      // Drop editor-internal raw-CSS attributes. They're meaningless outside
      // of TipTap's NodeView and confuse downstream email renderers.
      delete attrs.containerstyle;
      delete attrs.containerStyle;
      delete attrs.wrapperstyle;
      delete attrs.wrapperStyle;

      const widthPx = extractWidthPx(containerStyle);
      const margin = extractMargin(containerStyle);

      // Build the inline style. Start from the configured base style (the
      // caller's `style` HTMLAttribute, e.g. "max-width: 100%; height: auto;"),
      // then layer width and margin on top. Email clients honor inline style.
      const baseStyle =
        typeof attrs.style === 'string' && attrs.style ? attrs.style : 'max-width: 100%; height: auto;';

      const styleParts: string[] = [];
      // Ensure max-width:100% / height:auto are present even if the caller
      // forgot — these are non-negotiable for mobile email rendering.
      styleParts.push(baseStyle);
      if (widthPx) {
        styleParts.push(`width: ${widthPx}px`);
      }
      if (margin) {
        styleParts.push(margin);
      }
      attrs.style = styleParts
        .join('; ')
        .replace(/;\s*;/g, ';') // collapse accidental double-semicolons
        .replace(/^\s*;\s*/, '') // strip leading semicolon
        .trim();

      // Width attribute (no `px` unit). Legacy Outlook honors this above CSS.
      if (widthPx) {
        // Parse to int then back to string to normalize "400.0" → "400".
        const widthInt = Math.round(parseFloat(widthPx));
        if (Number.isFinite(widthInt) && widthInt > 0) {
          attrs.width = String(widthInt);
        }
      }

      return ['img', attrs];
    },
  }).configure({
    inline,
    allowBase64,
    minWidth,
    ...(maxWidth !== undefined ? { maxWidth } : {}),
    HTMLAttributes: {
      class: htmlClass,
      // Inline style so it survives in plain HTML email clients.
      // Width is layered on top of this at render time when the user resizes.
      style: 'max-width: 100%; height: auto;',
    },
  });
}
